import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { Autocomplete, TextField } from '@mui/material';
import './CreateMarketplaceItem.css';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import MsvPageHeadWithBack from '../../components/MsvPageHeadWithBack/MsvPageHeadWithBack';
import MsvSelect from '../../components/MsvSelect/MsvSelect';
import JSZip from 'jszip';
import { useRequireLogin } from '../../utils/requireLogin';
import { apiFetch } from '../../utils/apiClient';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { getCollectiblePrefill } from '../../utils/collectiblePrefill';
import { isAndroidCapacitor, isAndroidTablet } from '../../utils/platformUtils';
import { reloadFixedLayoutAds } from '../../utils/fixedLayoutAds';
import FixedLayoutAds from '../../components/FixedLayoutAds/FixedLayoutAds';
import ImagePicker from '../../components/ImagePicker/ImagePicker';
import PageLoader from '../../components/PageLoader/PageLoader';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import {
    driverIdPairs,
    seriesIdPairs,
    mainCategoriesIdPairs,
    diecastSubCategoriesIdPairs,
    raceUsedSubCategoriesIdPairs,
    driverWornSubCategoriesIdPairs,
    sheetMetalSubCategoriesIdPairs,
    teamIssuedSubCategoriesIdPairs,
    diecastYearCategoriesIdPairs,
    diecastVersionCategoriesIdPairs,
    tradingCardYearCategoriesIdPairs,
    tradingCardBrandCategoriesIdPairs,
    tradingCardTypeCategoriesIdPairs,
    heroCardYearCategoriesIdPairs,
    programsTicketsYearCategoriesIdPairs
} from '../../constants/dropdownIdPairs';
import { US_STATES } from '../../constants/usStates';

const STATE_OPTIONS = US_STATES;

const initialMarketplaceFormData = {
    driverName: '',
    title: '',
    description: '',
    price: '',
    shippingAvailable: false,
    localPickupAvailable: true,
    shippingCost: '',
    city: '',
    state: '',
    url: '',
    series: '',
    mainCategory: '',
    subCategory: '',
    subSubCategory: '',
    subSubSubCategory: '',
};

const CreateMarketplaceItem = () => {
    const { user } = useContext(UserContext);


    // State to handle form inputs
    const { authReady, isAuthenticated } = useRequireLogin('Please log in to create a marketplace listing.');
    const [formData, setFormData] = useState(initialMarketplaceFormData);

    const navigate = useNavigate();
    const location = useLocation();
    const initialCollectionItemId = location.state?.collectionItemId || null;
    const pickFromCollection =
        location.state?.pickFromCollection === true ||
        new URLSearchParams(location.search).get('fromCollection') === '1';

    const [sourceCollectionItemId, setSourceCollectionItemId] = useState(initialCollectionItemId);
    const [showCollectionPicker, setShowCollectionPicker] = useState(pickFromCollection && !initialCollectionItemId);
    const [showListingSummary, setShowListingSummary] = useState(Boolean(initialCollectionItemId));
    const [collectionPickerItems, setCollectionPickerItems] = useState([]);
    const [loadingCollectionPicker, setLoadingCollectionPicker] = useState(false);
    const [loadingSourceCollectible, setLoadingSourceCollectible] = useState(Boolean(initialCollectionItemId));
    const [loadingPickerItemId, setLoadingPickerItemId] = useState(null);
    const [sourceCollectibleTitle, setSourceCollectibleTitle] = useState('');
    const [sourceCollectibleImagePreview, setSourceCollectibleImagePreview] = useState('');
    const [sourceCollectibleImage2Preview, setSourceCollectibleImage2Preview] = useState('');
    const [showChangeItemConfirm, setShowChangeItemConfirm] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const loadRequestIdRef = useRef(0);
    const consumedInitialCollectionItemIdRef = useRef(null);

    const clearListingNavigationState = (nextState = { pickFromCollection: true }) => {
        navigate(location.pathname, { replace: true, state: nextState });
    };

    const [errors, setErrors] = useState({});
    const [successMessage] = useState('');
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [dragOver, setDragOver] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([null]);

    const SECOND_IMAGE_CATEGORIES = ['Diecasts', 'Race Used / Team Issued'];
    // Match vault: unlock for Diecasts / Race Used, and while category is still empty.
    const canAddSecondImage =
        SECOND_IMAGE_CATEGORIES.includes(formData.mainCategory) || !formData.mainCategory;
    const showSecondImageSlot = true;
    const secondImageLockHint = formData.mainCategory === 'Trading Cards'
        ? 'Not available for Trading Cards'
        : formData.mainCategory === 'Hero Cards / Post Cards'
            ? 'Not available for Hero Cards'
            : 'Available for Diecasts & Race Used';

    useEffect(() => {
        if (!canAddSecondImage) {
            setUploadedImages((prevImages) => {
                if (prevImages.length < 2) return prevImages;
                return [prevImages[0] || null];
            });
        }
    }, [formData.mainCategory, canAddSecondImage]);

    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);

    const [series, setSeries] = useState([]);
    const [selectedSeries, setSelectedSeries] = useState(null);

    const [mainCategories, setMainCategories] = useState([]);
    const [selectedMainCategory, setSelectedMainCategory] = useState(null);

    const [diecastSubCategories, setDiecastSubCategories] = useState([]);
    const [selectedDiecastSubCategory, setSelectedDiecastSubCategory] = useState(null);

    const [raceUsedSubCategories, setraceUsedSubCategories] = useState([]);
    const [selectedraceUsedSubCategory, setSelectedraceUsedSubCategory] = useState(null);

    const [driverWornSubCategories, setdriverWornSubCategories] = useState([]);
    const [selecteddriverWornSubCategory, setSelectedDriverWornSubCategory] = useState(null);

    const [sheetMetalSubCategories, setsheetMetalSubCategories] = useState([]);
    const [selectedsheetMetalSubCategory, setSelectedSheetMetalSubCategory] = useState(null);

    const [teamIssuedSubCategories, setTeamIssuedSubCategories] = useState([]);
    const [selectedTeamIssuedSubCategory, setSelectedTeamIssueSubCategory] = useState(null);

    const [diecastYearCategories, setDiecastYearCategories] = useState([]);
    const [selectedDiecastYearCategory, setSelectedDiecastYearCategory] = useState(null);

    const [diecastVersionCategories, setDiecastVersionCategories] = useState([]);
    const [selectedDiecastVersionCategory, setSelectedDiecastVersionCategory] = useState(null);

    const [tradingCardYearCategories, setTradingCardYearCategories] = useState([]);
    const [selectedTradingCardYearCategory, setSelectedTradingCardYearCategory] = useState(null);

    const [tradingCardBrandCategories, setTradingCardBrandCategories] = useState([]);
    const [selectedTradingCardBrandCategory, setSelectedTradingCardBrandCategory] = useState(null);

    const [tradingCardTypeCategories, setTradingCardTypeCategories] = useState([]);
    const [selectedTradingCardTypeCategory, setSelectedTradingCardTypeCategory] = useState(null);

    const [heroCardYearCategories, setHeroCardYearCategories] = useState([]);
    const [selectedHeroCardYearCategory, setSelectedHeroCardYearCategory] = useState(null);

    const [progamTicketYearCategories, setProgramTicketYearCategories] = useState([]);
    const [selectedProgramTicketYearCategory, setSelectedProgramTicketYearCategory] = useState(null);
    const [categoryNotRelevant, setCategoryNotRelevant] = useState(false);

    const clearDriverAndCategorySelections = () => {
        setSelectedDriver(null);
        setSelectedSeries(null);
        setSelectedMainCategory(null);
        setSelectedDiecastSubCategory(null);
        setSelectedraceUsedSubCategory(null);
        setSelectedSheetMetalSubCategory(null);
        setSelectedDriverWornSubCategory(null);
        setSelectedTeamIssueSubCategory(null);
        setSelectedDiecastYearCategory(null);
        setSelectedDiecastVersionCategory(null);
        setSelectedTradingCardYearCategory(null);
        setSelectedTradingCardBrandCategory(null);
        setSelectedTradingCardTypeCategory(null);
        setSelectedHeroCardYearCategory(null);
        setSelectedProgramTicketYearCategory(null);
        setFormData((prev) => ({
            ...prev,
            driverName: '',
            series: '',
            mainCategory: '',
            subCategory: '',
            subSubCategory: '',
            subSubSubCategory: '',
        }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next.driverName;
            delete next.series;
            delete next.mainCategory;
            return next;
        });
    };

    const handleCategoryNotRelevantChange = (checked) => {
        setCategoryNotRelevant(checked);
        if (checked) {
            clearDriverAndCategorySelections();
        }
    };

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production' || !user) return;

        if (!isAndroidCapacitor()) {
            reloadFixedLayoutAds();
        }
    }, [user, showCollectionPicker]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !user) return;

        const fetchDrivers = async () => {
            try {
                setDrivers(driverIdPairs.sort((a, b) =>
                    a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
                ));

            } catch (error) {
                console.error('Error fetching drivers:', error);
            }
        };

        const fetchSeries = async () => {
            try {
                setSeries(seriesIdPairs.sort((a, b) =>
                    a.seriesName.toLowerCase().localeCompare(b.seriesName.toLowerCase())
                ));

            } catch (error) {
                console.error('Error fetching series:', error);
            }
        };

        const fetchMainCategories = async () => {
            try {
                setMainCategories(mainCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching main categories:', error);
            }
        };

        const fetchDiecastSubCategories = async () => {
            try {
                setDiecastSubCategories(diecastSubCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching diecast sub categories:', error);
            }
        };

        const fetchRaceUsedSubCategories = async () => {
            try {
                setraceUsedSubCategories(raceUsedSubCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching race used sub categories:', error);
            }
        };

        const fetchDriverWornSubCategories = async () => {
            try {
                setdriverWornSubCategories(driverWornSubCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching driver worn sub categories:', error);
            }
        };

        const fetchSheetMetalSubCategories = async () => {
            try {
                setsheetMetalSubCategories(sheetMetalSubCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching sheet metal sub categories:', error);
            }
        };

        const fetchTeamIssuedSubCategories = async () => {
            try {
                setTeamIssuedSubCategories(teamIssuedSubCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching team issued sub categories:', error);
            }
        };

        const fetchDiecastYearCategoriesIdPairs = async () => {
            try {
                const sortedPairs = [...diecastYearCategoriesIdPairs].sort((a, b) => {
                    return parseInt(b.name) - parseInt(a.name);
                });

                setDiecastYearCategories(sortedPairs);
            } catch (error) {
                console.error('Error fetching diecast year categories:', error);
            }
        };

        const fetchDiecastVersionCategories = async () => {
            try {
                const sortedPairs = [...diecastVersionCategoriesIdPairs].sort((a, b) => {
                    if (a.name === "Standard Base Version") return -1;
                    if (b.name === "Standard Base Version") return 1;
                    return a.name.localeCompare(b.name);
                });

                setDiecastVersionCategories(sortedPairs);
            } catch (error) {
                console.error('Error fetching diecast version categories:', error);
            }
        };

        const fetchTradingCardYearCategories = async () => {
            try {
                const sortedPairs = [...tradingCardYearCategoriesIdPairs].sort((a, b) => {
                    return parseInt(b.name) - parseInt(a.name);
                });

                setTradingCardYearCategories(sortedPairs);
            } catch (error) {
                console.error('Error fetching trading card year categories:', error);
            }
        };

        const fetchTradingCardBrandCategories = async () => {
            try {
                setTradingCardBrandCategories(tradingCardBrandCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching trading card brand categories:', error);
            }
        };

        const fetchTradingCardTypeCategories = async () => {
            try {
                setTradingCardTypeCategories(tradingCardTypeCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching trading card type categories:', error);
            }
        };


        const fetchHeroCardYearCategories = async () => {
            try {
                setHeroCardYearCategories(heroCardYearCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching hero card year categories:', error);
            }
        };

        const fetchProgramTicketYearCategories = async () => {
            try {
                setProgramTicketYearCategories(programsTicketsYearCategoriesIdPairs);

            } catch (error) {
                console.error('Error fetching program ticket year categories:', error);
            }
        };

        fetchDrivers();
        fetchSeries();
        fetchMainCategories();
        fetchDiecastSubCategories();
        fetchRaceUsedSubCategories();
        fetchDriverWornSubCategories();
        fetchSheetMetalSubCategories();
        fetchTeamIssuedSubCategories();
        fetchDiecastYearCategoriesIdPairs();
        fetchDiecastVersionCategories();
        fetchTradingCardYearCategories();
        fetchTradingCardBrandCategories();
        fetchTradingCardTypeCategories();
        fetchHeroCardYearCategories();
        fetchProgramTicketYearCategories();
    }, [authReady, isAuthenticated, user]);

    const applyCollectiblePrefill = (data) => {
        const { formFields, selected } = getCollectiblePrefill(data);
        setFormData((prev) => ({ ...prev, ...formFields }));
        setSelectedDriver(selected.driver);
        setSelectedSeries(selected.series);
        setSelectedMainCategory(selected.mainCategory);
        setSelectedDiecastSubCategory(selected.diecastSubCategory);
        setSelectedraceUsedSubCategory(selected.raceUsedSubCategory);
        setSelectedSheetMetalSubCategory(selected.sheetMetalSubCategory);
        setSelectedDriverWornSubCategory(selected.driverWornSubCategory);
        setSelectedTeamIssueSubCategory(selected.teamIssuedSubCategory);
        setSelectedDiecastYearCategory(selected.diecastYearCategory);
        setSelectedDiecastVersionCategory(selected.diecastVersionCategory);
        setSelectedTradingCardYearCategory(selected.tradingCardYearCategory);
        setSelectedTradingCardBrandCategory(selected.tradingCardBrandCategory);
        setSelectedTradingCardTypeCategory(selected.tradingCardTypeCategory);
        setSelectedHeroCardYearCategory(selected.heroCardYearCategory);
        setSelectedProgramTicketYearCategory(selected.programTicketYearCategory);
        setSourceCollectibleTitle(data.title || '');
    };

    const resetCollectionSource = () => {
        loadRequestIdRef.current += 1;
        consumedInitialCollectionItemIdRef.current = null;
        setShowChangeItemConfirm(false);
        setShowCollectionPicker(true);
        setShowListingSummary(false);
        setSourceCollectionItemId(null);
        setSourceCollectibleTitle('');
        setSourceCollectibleImagePreview('');
        setSourceCollectibleImage2Preview('');
        setUploadedImages([null]);
        setLoadingSourceCollectible(false);
        setLoadingPickerItemId(null);
        setFormData({ ...initialMarketplaceFormData });
        clearDriverAndCategorySelections();
        setErrors({});
        setApiErrorMessage('');
        // Drop stale router state so a prior "sell this item" ID cannot reload over a new pick.
        clearListingNavigationState({ pickFromCollection: true });
    };

    const loadCollectibleForListing = async (collectionItemId) => {
        if (!user?.userId || !collectionItemId) return;

        const requestId = ++loadRequestIdRef.current;
        setLoadingSourceCollectible(true);
        setLoadingPickerItemId(collectionItemId);
        setApiErrorMessage('');
        try {
            const response = await apiFetch('/Collectibles/GetCollectionItemById', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    CollectionItemId: collectionItemId,
                    UserId: user.userId,
                }),
            });

            if (requestId !== loadRequestIdRef.current) return;

            if (!response.ok) {
                setApiErrorMessage('Unable to load collectible. It may have already been listed.');
                setShowListingSummary(false);
                return;
            }

            const data = await response.json();
            if (requestId !== loadRequestIdRef.current) return;

            applyCollectiblePrefill(data);
            setSourceCollectionItemId(collectionItemId);
            setShowCollectionPicker(false);
            setShowListingSummary(true);
            setUploadedImages([null]);
            setSourceCollectibleImagePreview(resolveImageSrc(data) || '');
            const secondImage =
                (typeof data.imageUrl2 === 'string' && data.imageUrl2) ||
                (typeof data.ImageUrl2 === 'string' && data.ImageUrl2) ||
                '';
            setSourceCollectibleImage2Preview(secondImage);
            consumedInitialCollectionItemIdRef.current = collectionItemId;
            clearListingNavigationState({ collectionItemId });
        } catch (err) {
            if (requestId !== loadRequestIdRef.current) return;
            setApiErrorMessage('Unable to load collectible details.');
            setShowListingSummary(false);
            console.error(err);
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoadingSourceCollectible(false);
                setLoadingPickerItemId(null);
            }
        }
    };

    useEffect(() => {
        if (!authReady || !isAuthenticated || !user) return;
        if (!initialCollectionItemId) return;
        if (consumedInitialCollectionItemIdRef.current === initialCollectionItemId) return;
        consumedInitialCollectionItemIdRef.current = initialCollectionItemId;
        loadCollectibleForListing(initialCollectionItemId);
    }, [authReady, isAuthenticated, user, initialCollectionItemId]);

    useEffect(() => {
        if (!showCollectionPicker || !authReady || !isAuthenticated || !user?.userId) return;

        let isMounted = true;
        const fetchUserCollectibles = async () => {
            setLoadingCollectionPicker(true);
            try {
                const response = await apiFetch('/Collectibles/UserSearchCollectibles', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        UserId: user.userId,
                        PageSize: 48,
                        PaginationToken: null,
                        HasLikes: false,
                    }),
                });

                if (!response.ok) throw new Error('Failed to load collection');

                const data = await response.json();
                if (isMounted) {
                    const items = (data.collectibleSearchResultDtos || []).filter(
                        (item) => !item.isForSale && !item.marketplaceItemId
                    );
                    setCollectionPickerItems(items);
                }
            } catch (err) {
                if (isMounted) {
                    setApiErrorMessage('Unable to load your collectibles.');
                    console.error(err);
                }
            } finally {
                if (isMounted) setLoadingCollectionPicker(false);
            }
        };

        fetchUserCollectibles();
        return () => { isMounted = false; };
    }, [showCollectionPicker, authReady, isAuthenticated, user]);

    async function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1]; // Strip 'data:application/zip;base64,'
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    const resizeImage = (dataUrl, scale) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                // You can also export to Blob if preferred:
                canvas.toBlob((blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                }, "image/jpeg", scale);
            };
            img.onerror = reject;
            img.src = dataUrl;
        });
    };

    const clearError = (errorKey) => {
        setErrors((prev) => {
            if (!prev[errorKey]) return prev;
            const next = { ...prev };
            delete next[errorKey];
            return next;
        });
    };

    // Function to handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'price') {
            if (value && parseFloat(value) > 0) clearError('price');
        } else if (typeof value === 'string' ? value.trim() : value) {
            clearError(name);
        }
    };

    const hasUploadedImage = uploadedImages.some((image) => image?.base64 && image?.mimeType);

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!sourceCollectionItemId && !hasUploadedImage) {
            newErrors.image = 'At least one photo is required';
        }
        if (!formData.title) {
            newErrors.title = 'Title is required';
        }
        if (!categoryNotRelevant) {
            if (!formData.driverName) {
                newErrors.driverName = 'Driver Name is required';
            }
            if (!formData.series) {
                newErrors.series = 'Series is required';
            }
            if (!formData.mainCategory) {
                newErrors.mainCategory = 'Main Category is required';
            }
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            newErrors.price = 'Valid price is required';
        }
        if (!formData.city) newErrors.city = 'Item city is required';
        if (!formData.state) newErrors.state = 'Item state is required';
        if (!formData.shippingAvailable && !formData.localPickupAvailable) {
            newErrors.fulfillment = 'Select shipping and/or local pickup';
        }
        if (!formData.description) newErrors.description = 'Description is required';
        setErrors(newErrors);
        return newErrors;
    };

    // Validate details fields before returning to the price/shipping summary
    const continueToListingSummary = () => {
        const newErrors = {};

        if (!sourceCollectionItemId && !hasUploadedImage) {
            newErrors.image = 'At least one photo is required';
        }
        if (!formData.title) {
            newErrors.title = 'Title is required';
        }
        if (!categoryNotRelevant) {
            if (!formData.driverName) {
                newErrors.driverName = 'Driver Name is required';
            }
            if (!formData.series) {
                newErrors.series = 'Series is required';
            }
            if (!formData.mainCategory) {
                newErrors.mainCategory = 'Main Category is required';
            }
        }
        if (!formData.description) newErrors.description = 'Description is required';

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setShowListingSummary(true);
    };

    // Validate first, then ask for confirmation before submitting
    const requestSubmitConfirm = () => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            const summaryFieldKeys = new Set(['price', 'city', 'state']);
            const needsFullForm = Object.keys(newErrors).some((key) => !summaryFieldKeys.has(key));
            if (showListingSummary && needsFullForm) {
                setShowListingSummary(false);
            }
            return;
        }

        setShowSubmitConfirm(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        requestSubmitConfirm();
    };

    const confirmSubmitListing = async () => {
        setSubmitting(true);
        try {
            let zipBase64 = null;
            if (hasUploadedImage) {
                const zip = new JSZip();

                for (let index = 0; index < uploadedImages.length; index++) {
                    if (index > 0 && !canAddSecondImage) continue;
                    const imageObj = uploadedImages[index];
                    if (imageObj && imageObj.base64 && imageObj.mimeType) {
                        let imageDataUrl = `data:${imageObj.mimeType};base64,${imageObj.base64}`;

                        let meetsSizeRequirements = false;
                        while (!meetsSizeRequirements) {
                            const base64SizeInBytes = Math.ceil((imageDataUrl.length - imageDataUrl.indexOf(',') - 1) * 3 / 4);
                            if (base64SizeInBytes > 40960) {
                                imageDataUrl = await resizeImage(imageDataUrl, 0.80);
                            } else {
                                meetsSizeRequirements = true;
                            }
                        }

                        const matches = imageDataUrl.match(/^data:(image\/[a-zA-Z]+);base64,(.+)$/);
                        if (!matches) {
                            console.warn(`Skipping image ${index + 1}: Invalid base64 format`);
                            continue;
                        }

                        const mimeType = matches[1];
                        const base64Data = matches[2];
                        const extension = mimeType.split('/')[1];

                        zip.file(`image${index + 1}.${extension}`, base64Data, { base64: true });
                    }
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                zipBase64 = await blobToBase64(zipBlob);
            }

            // Do not send userId in the body — UserId is a string on the DTO and a numeric
            // JSON value fails System.Text.Json binding (400). The API sets it from the JWT.
            const response = await apiFetch('/Marketplace/SubmitNewMarketplaceItem', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        driverName: categoryNotRelevant ? '' : formData.driverName,
                        title: formData.title,
                        description: formData.description,
                        price: parseFloat(formData.price),
                        shippingAvailable: formData.shippingAvailable,
                        localPickupAvailable: formData.localPickupAvailable,
                        shippingCost: formData.shippingAvailable && formData.shippingCost
                            ? parseFloat(formData.shippingCost)
                            : null,
                        city: formData.city,
                        state: formData.state,
                        url: formData.url || null,
                        series: categoryNotRelevant ? '' : formData.series,
                        mainCategory: categoryNotRelevant ? 'Misc.' : formData.mainCategory,
                        subCategory: categoryNotRelevant ? '' : formData.subCategory,
                        subSubCategory: categoryNotRelevant ? '' : formData.subSubCategory,
                        subSubSubCategory: categoryNotRelevant ? '' : formData.subSubSubCategory,
                        zippedImages: zipBase64 ? `data:application/zip;base64,${zipBase64}` : null,
                        collectionItemId: sourceCollectionItemId ? Number(sourceCollectionItemId) : null,
                    })
                });

                if (!response.ok) {
                    let detail = '';
                    try {
                        const errBody = await response.json();
                        const fieldErrors = errBody?.errors
                            ? Object.values(errBody.errors).flat().filter(Boolean)
                            : [];
                        detail =
                            fieldErrors[0] ||
                            errBody?.title ||
                            errBody?.detail ||
                            errBody?.message ||
                            '';
                    } catch {
                        /* ignore */
                    }
                    setApiErrorMessage(
                        detail ||
                        (sourceCollectionItemId
                            ? 'Unable to list this collectible. It may already be on the marketplace.'
                            : 'Unable to create marketplace listing. Please try again.')
                    );
                    setShowSubmitConfirm(false);
                    return;
                }

                const submitResult = await response.json();
                if (!submitResult) {
                    setApiErrorMessage(
                        sourceCollectionItemId
                            ? 'Unable to list this collectible. It may already be on the marketplace.'
                            : 'Unable to create marketplace listing. Please try again.'
                    );
                    setShowSubmitConfirm(false);
                    return;
                }

                setFormData({ ...initialMarketplaceFormData });
                setCategoryNotRelevant(false);
                setErrors({});
                setApiErrorMessage('');
                setShowSubmitConfirm(false);
            navigate('/mymarketplacelistings', {
                state: { message: 'Your listing has been submitted and is under review.' },
            });
        } catch (err) {
            setApiErrorMessage('An unexpected error occurred. Please try again.');
            setShowSubmitConfirm(false);
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (event, index) => {
        event.preventDefault();
        setDragOver(false);
        const file = event.dataTransfer.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64String = e.target.result.split(',')[1];
                const mimeType = e.target.result.split(';')[0].split(':')[1];
                setUploadedImages((prevImages) => {
                    const newImages = [...prevImages];
                    newImages[index] = { base64: base64String, mimeType };
                    return newImages;
                });
                clearError('image');
            };
            reader.readAsDataURL(file);
        }
    };

    // Handler for ImagePicker component
    const handleImagePickerFileReady = (file, index) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const base64String = e.target.result.split(',')[1];
            const mimeType = e.target.result.split(';')[0].split(':')[1];
            setUploadedImages((prevImages) => {
                const newImages = [...prevImages];
                newImages[index] = { base64: base64String, mimeType };
                return newImages;
            });
            clearError('image');
        };
        reader.readAsDataURL(file);
    };

    if (!authReady || !isAuthenticated) return null;

    const handlePageBack = () => navigate(-1);

    const renderMarketplacePageHead = (title) => (
        <MsvPageHeadWithBack
            eyebrow="Marketplace"
            title={title}
            onBack={handlePageBack}
        />
    );

    const renderPageWithAds = (pageContent) => (
        <FixedLayoutAds
            bottomSlot="3311717092"
            sidebarSlot="5678860136"
            fullWidth={isAndroidTablet() || isAndroidCapacitor()}
        >
            {pageContent}
            <ConfirmDialog
                open={showChangeItemConfirm}
                title="Choose a different item?"
                message="Your current progress will be lost if you continue."
                confirmLabel="Proceed"
                cancelLabel="Go back"
                onConfirm={resetCollectionSource}
                onCancel={() => setShowChangeItemConfirm(false)}
            />
            <ConfirmDialog
                open={showSubmitConfirm}
                variant="success"
                title={sourceCollectionItemId ? 'List for sale?' : 'Add to Marketplace?'}
                message="Your listing will be reviewed before it goes live the first time. After that, you can edit details anytime without another review. The item stays in your collection."
                confirmLabel={sourceCollectionItemId ? 'List for Sale' : 'Add to Marketplace'}
                cancelLabel="Go back"
                busyLabel={sourceCollectionItemId ? 'Moving…' : 'Adding…'}
                busy={submitting}
                onConfirm={confirmSubmitListing}
                onCancel={() => !submitting && setShowSubmitConfirm(false)}
            />
        </FixedLayoutAds>
    );

    if (showCollectionPicker) {
        return renderPageWithAds(
                <div className="main-vla-container create-marketplace-page">
                    <ScrollToTop />
                    {renderMarketplacePageHead('Sell from your collection')}
                    <p className="form-section-hint">Choose a collectible to list for sale. It stays in your collection and shows a For Sale badge after approval.</p>
                    {apiErrorMessage && <p className="sign-up-error">{apiErrorMessage}</p>}
                    {loadingCollectionPicker ? (
                        <PageLoader message="Loading your collectibles…" fullHeight={false} />
                    ) : collectionPickerItems.length === 0 ? (
                        <div className="cmi-picker-empty">
                            <p>You do not have any collectibles to sell yet.</p>
                            <button type="button" className="login-submit-btn" onClick={() => navigate('/additems')}>
                                Add a collectible
                            </button>
                            <button type="button" className="view-button cmi-picker-secondary" onClick={() => setShowCollectionPicker(false)}>
                                List a new item instead
                            </button>
                        </div>
                    ) : (
                        <div className={`cmi-picker-grid${loadingSourceCollectible ? ' cmi-picker-grid--loading' : ''}`}>
                            {collectionPickerItems.map((item) => {
                                const isLoadingThis = loadingPickerItemId === item.collectionItemId;
                                return (
                                    <button
                                        key={item.collectionItemId}
                                        type="button"
                                        className={`cmi-picker-card${isLoadingThis ? ' cmi-picker-card--loading' : ''}`}
                                        onClick={() => loadCollectibleForListing(item.collectionItemId)}
                                        disabled={loadingSourceCollectible}
                                        aria-busy={isLoadingThis}
                                    >
                                        <span className="cmi-picker-card-media">
                                            <img src={resolveImageSrc(item)} alt={item.title} />
                                            {isLoadingThis && (
                                                <span className="cmi-picker-card-loading" aria-hidden="true">
                                                    <span className="cmi-picker-card-spinner" />
                                                    <span>Loading…</span>
                                                </span>
                                            )}
                                        </span>
                                        <span>{toTitleCase(item.title)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {!loadingCollectionPicker && collectionPickerItems.length > 0 && (
                        <button
                            type="button"
                            className="view-button cmi-picker-secondary"
                            onClick={() => setShowCollectionPicker(false)}
                            disabled={loadingSourceCollectible}
                        >
                            List a new item instead
                        </button>
                    )}
                </div>
        );
    }

    const categoryPath = [
        formData.mainCategory,
        formData.subCategory,
        formData.subSubCategory,
        formData.subSubSubCategory,
    ].filter(Boolean).join(' · ');

    if (loadingSourceCollectible && !showCollectionPicker) {
        return renderPageWithAds(
            <div className="main-vla-container create-marketplace-page">
                <ScrollToTop />
                {renderMarketplacePageHead('Sell an Item')}
                <PageLoader message="Loading collectible details…" fullHeight={false} />
            </div>
        );
    }

    if (sourceCollectionItemId && showListingSummary) {
        return renderPageWithAds(
            <div className="main-vla-container create-marketplace-page">
                <ScrollToTop />
                {renderMarketplacePageHead('Confirm listing')}
                <p className="form-section-hint">
                    Review the details from your collection, set a price, then list. Edit anything that needs changing.
                </p>
                {apiErrorMessage && <p className="sign-up-error">{apiErrorMessage}</p>}

                <section className="cmi-listing-summary form-section-card">
                    <div className="cmi-listing-summary-top">
                        {sourceCollectibleImagePreview ? (
                            <img
                                src={sourceCollectibleImagePreview}
                                alt={sourceCollectibleTitle || 'Collectible'}
                                className="cmi-listing-summary-photo"
                            />
                        ) : (
                            <div className="cmi-listing-summary-photo cmi-listing-summary-photo--empty" aria-hidden="true" />
                        )}
                        <div className="cmi-listing-summary-copy">
                            <h2 className="cmi-listing-summary-title">
                                {toTitleCase(formData.title || sourceCollectibleTitle) || 'Untitled collectible'}
                            </h2>
                            {formData.description && (
                                <p className="cmi-listing-summary-description">{formData.description}</p>
                            )}
                            <dl className="cmi-listing-summary-meta">
                                {formData.driverName && (
                                    <>
                                        <dt>Driver</dt>
                                        <dd>{formData.driverName}</dd>
                                    </>
                                )}
                                {formData.series && (
                                    <>
                                        <dt>Series</dt>
                                        <dd>{formData.series}</dd>
                                    </>
                                )}
                                {categoryPath && (
                                    <>
                                        <dt>Category</dt>
                                        <dd>{categoryPath}</dd>
                                    </>
                                )}
                            </dl>
                            <button
                                type="button"
                                className="cmi-change-source"
                                onClick={() => setShowListingSummary(false)}
                            >
                                Edit listing details
                            </button>
                        </div>
                    </div>
                </section>

                <form onSubmit={handleSubmit}>
                    <section className="form-section-card">
                        <h2 className="form-section-title">Price &amp; Shipping</h2>
                        <p className="form-section-hint">Sale details for this listing — your collectible stays in the vault and shows For Sale after approval.</p>
                        <div className="sign-up-form-group">
                            <label>Price ($)<span className="asterisk">*</span></label>
                            <input type="number" name="price" min="0" step="0.01" value={formData.price} onChange={handleChange} className={errors.price ? 'error' : ''} />
                            {errors.price && <p className="sign-up-error">{errors.price}</p>}
                        </div>
                        <div className="sign-up-form-group">
                            <label htmlFor="marketplace-state-summary">Item State<span className="asterisk">*</span></label>
                            <MsvSelect
                                id="marketplace-state-summary"
                                name="state"
                                fullWidth
                                filterable
                                placeholder="Type or select item state…"
                                value={formData.state}
                                options={STATE_OPTIONS}
                                onChange={handleChange}
                            />
                            {errors.state && <p className="sign-up-error">{errors.state}</p>}
                        </div>
                        <div className="sign-up-form-group">
                            <label>Item City<span className="asterisk">*</span></label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} className={errors.city ? 'error' : ''} />
                            {errors.city && <p className="sign-up-error">{errors.city}</p>}
                        </div>
                        <div className="cmi-fulfillment-row">
                            <div className="sign-up-form-group">
                                <label>
                                    Local pickup available
                                    <input type="checkbox" name="localPickupAvailable" checked={Boolean(formData.localPickupAvailable)} onChange={(e) => setFormData((p) => ({ ...p, localPickupAvailable: e.target.checked }))} />
                                </label>
                            </div>
                            <div className="sign-up-form-group">
                                <label>
                                    Shipping available
                                    <input type="checkbox" name="shippingAvailable" checked={Boolean(formData.shippingAvailable)} onChange={(e) => setFormData((p) => ({ ...p, shippingAvailable: e.target.checked }))} />
                                </label>
                            </div>
                        </div>
                        {errors.fulfillment && <p className="sign-up-error">{errors.fulfillment}</p>}
                        <div className="sign-up-form-group">
                            <label>Shipping cost ($)</label>
                            <input
                                type="number"
                                name="shippingCost"
                                min="0"
                                step="0.01"
                                value={formData.shippingCost}
                                onChange={handleChange}
                                disabled={!formData.shippingAvailable}
                                aria-disabled={!formData.shippingAvailable}
                            />
                        </div>
                    </section>

                    <div className="cmi-summary-actions">
                        <button
                            type="button"
                            className={`login-submit-btn msv-shine${submitting ? ' disabled' : ''}`}
                            disabled={submitting}
                            onClick={requestSubmitConfirm}
                        >
                            {submitting ? 'Listing for sale…' : 'List for Sale'}
                        </button>
                        <button
                            type="button"
                            className="view-button cmi-picker-secondary"
                            onClick={() => setShowChangeItemConfirm(true)}
                        >
                            Choose a different item
                        </button>
                    </div>
                </form>
                {successMessage && <p className="sign-up-success">{successMessage}</p>}
            </div>
        );
    }

    return renderPageWithAds(
            <div className="main-vla-container create-marketplace-page">
                <ScrollToTop />
                {renderMarketplacePageHead('Sell an Item')}
                {sourceCollectionItemId && (
                    <div className="cmi-from-collection-banner">
                        <p>
                            Listing <strong>{toTitleCase(sourceCollectibleTitle) || 'this collectible'}</strong> for sale. It stays in your collection and shows For Sale after approval.
                        </p>
                        <div className="cmi-from-collection-banner-actions">
                            <button
                                type="button"
                                className="cmi-change-source"
                                onClick={() => setShowListingSummary(true)}
                            >
                                Back to summary
                            </button>
                            <button
                                type="button"
                                className="cmi-change-source"
                                onClick={() => setShowChangeItemConfirm(true)}
                            >
                                Choose a different item
                            </button>
                        </div>
                    </div>
                )}
                {!sourceCollectionItemId && (
                    <button
                        type="button"
                        className="cmi-sell-from-collection-link"
                        onClick={() => setShowCollectionPicker(true)}
                    >
                        Sell something from my collection
                    </button>
                )}
                {apiErrorMessage && <p className="sign-up-error">{apiErrorMessage}</p>}
                <form onSubmit={handleSubmit}>
                    <p className="required-note">Fields marked with <span className="asterisk">*</span> are required.</p>

                    {/* Item Image Upload */}
                    <section className="form-section-card">
                    <h2 className="form-section-title">Photos</h2>
                    <p className="form-section-hint">
                        {sourceCollectionItemId
                            ? (sourceCollectibleImage2Preview
                                ? 'Your collection photos will be used automatically. Upload only if you want different photos.'
                                : 'Your collection photo will be used automatically. Upload only if you want a different photo.')
                            : 'Clear, well-lit photos sell faster.'}
                    </p>
                    <div className="sign-up-form-group">
                        <label>
                            Item Image{!sourceCollectionItemId && <span className="asterisk">*</span>}
                        </label>
                    </div>
                    {errors.image && <p className="sign-up-error">{errors.image}</p>}
                    <div className="form-group item-image">
                        <div className='row add-collectible-photo-slots'>
                            {uploadedImages.slice(0, 1).map((image, index) => (
                                <div
                                    key={index}
                                    className={`ship-image-popup-drag-drop ${dragOver ? 'drag-over' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(event) => handleDrop(event, index)}
                                >
                                    {image ? (
                                        <div className="ship-image-uploaded-image-container">
                                            <button
                                                className="ship-image-popup-remove-image"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setUploadedImages((prevImages) => {
                                                        const newImages = [...prevImages];
                                                        newImages[index] = null;
                                                        return newImages;
                                                    });
                                                }}
                                                title="Remove image"
                                            >
                                                X
                                            </button>
                                            <img
                                                src={`data:${image.mimeType};base64,${image.base64}`}
                                                alt="Uploaded"
                                                className="ship-image-popup-uploaded-image-container"
                                                title="Click to replace image"
                                            />
                                        </div>
                                    ) : sourceCollectionItemId && sourceCollectibleImagePreview && index === 0 ? (
                                        <div className="cmi-collection-photo-preview">
                                            <img
                                                src={sourceCollectibleImagePreview}
                                                alt="Collection item"
                                                className="cmi-collection-photo-img"
                                            />
                                            <p className="cmi-collection-photo-label">Photo from your collection</p>
                                            <div className="image-replace-button">
                                                <ImagePicker
                                                    onFileReady={(file) => handleImagePickerFileReady(file, index)}
                                                    buttonText="Replace photo"
                                                    className="replace-button"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="image-upload-placeholder">
                                            <p>Drag & Drop an image or use the button below</p>
                                            <ImagePicker 
                                                onFileReady={(file) => handleImagePickerFileReady(file, index)}
                                                buttonText="Add photo"
                                            />
                                        </div>
                                    )}
                                    {image && (
                                        <div className="image-replace-button">
                                            <ImagePicker 
                                                onFileReady={(file) => handleImagePickerFileReady(file, index)}
                                                buttonText="Replace photo"
                                                className="replace-button"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {showSecondImageSlot && (
                                canAddSecondImage ? (
                                    <div
                                        className={`ship-image-popup-drag-drop ${dragOver ? 'drag-over' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(event) => handleDrop(event, 1)}
                                    >
                                        {uploadedImages[1] ? (
                                            <div className="ship-image-uploaded-image-container">
                                                <button
                                                    className="ship-image-popup-remove-image"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setUploadedImages((prevImages) => {
                                                            const newImages = [...prevImages];
                                                            while (newImages.length < 2) newImages.push(null);
                                                            newImages[1] = null;
                                                            return newImages;
                                                        });
                                                    }}
                                                    title="Remove image"
                                                >
                                                    X
                                                </button>
                                                <img
                                                    src={`data:${uploadedImages[1].mimeType};base64,${uploadedImages[1].base64}`}
                                                    alt="Second uploaded"
                                                    className="ship-image-popup-uploaded-image-container"
                                                />
                                            </div>
                                        ) : sourceCollectionItemId && sourceCollectibleImage2Preview ? (
                                            <div className="cmi-collection-photo-preview">
                                                <img
                                                    src={sourceCollectibleImage2Preview}
                                                    alt="Collection item second"
                                                    className="cmi-collection-photo-img"
                                                />
                                                <p className="cmi-collection-photo-label">Photo 2 from your collection</p>
                                                <div className="image-replace-button">
                                                    <ImagePicker
                                                        onFileReady={(file) => handleImagePickerFileReady(file, 1)}
                                                        buttonText="Replace photo"
                                                        className="replace-button"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="image-upload-placeholder">
                                                <p>Second photo (optional)</p>
                                                <ImagePicker
                                                    onFileReady={(file) => handleImagePickerFileReady(file, 1)}
                                                    buttonText="Add second photo"
                                                />
                                            </div>
                                        )}
                                        {uploadedImages[1] && (
                                            <div className="image-replace-button">
                                                <ImagePicker
                                                    onFileReady={(file) => handleImagePickerFileReady(file, 1)}
                                                    buttonText="Replace photo"
                                                    className="replace-button"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className="ship-image-popup-drag-drop second-image-slot second-image-slot--locked"
                                        title={secondImageLockHint}
                                    >
                                        <div className="second-image-lock-icon" aria-hidden="true">🔒</div>
                                        <p>Second photo locked</p>
                                        <p className="second-image-lock-hint">{secondImageLockHint}</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                    </section>

                    <section className="form-section-card">
                    <h2 className="form-section-title">Details</h2>
                    <div className="sign-up-form-group">
                        <label>
                            Title<span className="asterisk">*</span>
                            <span style={{ float: 'right', fontSize: '0.9em' }}>
                                {(formData.title || '').length}/80
                            </span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={(e) => {
                                if (e.target.value.length <= 80) {
                                    handleChange(e);
                                }
                            }}
                            className={errors.title ? "error" : ""}
                        />
                        {errors.title && <p className="sign-up-error">{errors.title}</p>}
                    </div>

                    <div className="sign-up-form-group">
                        <label>Description<span className="asterisk">*</span></label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={4}
                            className={errors.description ? 'error' : ''}
                        />
                        {errors.description && <p className="sign-up-error">{errors.description}</p>}
                    </div>
                    </section>

                    <section className="form-section-card">
                    <h2 className="form-section-title">Price &amp; Shipping</h2>
                    <div className="sign-up-form-group">
                        <label>Price ($)<span className="asterisk">*</span></label>
                        <input type="number" name="price" min="0" step="0.01" value={formData.price} onChange={handleChange} className={errors.price ? 'error' : ''} />
                        {errors.price && <p className="sign-up-error">{errors.price}</p>}
                    </div>
                    <div className="sign-up-form-group">
                        <label htmlFor="marketplace-state">Item State<span className="asterisk">*</span></label>
                        <MsvSelect
                            id="marketplace-state"
                            name="state"
                            fullWidth
                            filterable
                            placeholder="Type or select item state…"
                            value={formData.state}
                            options={STATE_OPTIONS}
                            onChange={handleChange}
                        />
                        {errors.state && <p className="sign-up-error">{errors.state}</p>}
                    </div>
                    <div className="sign-up-form-group">
                        <label>Item City<span className="asterisk">*</span></label>
                        <input type="text" name="city" value={formData.city} onChange={handleChange} className={errors.city ? 'error' : ''} />
                        {errors.city && <p className="sign-up-error">{errors.city}</p>}
                    </div>
                    <div className="cmi-fulfillment-row">
                        <div className="sign-up-form-group">
                            <label>
                                Local pickup available
                                <input type="checkbox" name="localPickupAvailable" checked={Boolean(formData.localPickupAvailable)} onChange={(e) => setFormData((p) => ({ ...p, localPickupAvailable: e.target.checked }))} />
                            </label>
                        </div>
                        <div className="sign-up-form-group">
                            <label>
                                Shipping available
                                <input type="checkbox" name="shippingAvailable" checked={Boolean(formData.shippingAvailable)} onChange={(e) => setFormData((p) => ({ ...p, shippingAvailable: e.target.checked }))} />
                            </label>
                        </div>
                    </div>
                    {errors.fulfillment && <p className="sign-up-error">{errors.fulfillment}</p>}
                    <div className="sign-up-form-group">
                        <label>Shipping cost ($)</label>
                        <input
                            type="number"
                            name="shippingCost"
                            min="0"
                            step="0.01"
                            value={formData.shippingCost}
                            onChange={handleChange}
                            disabled={!formData.shippingAvailable}
                            aria-disabled={!formData.shippingAvailable}
                        />
                    </div>
                    <div className="sign-up-form-group">
                        <label>External URL (optional)</label>
                        <input type="text" name="url" value={formData.url} onChange={handleChange} />
                    </div>
                    </section>

                    <section className="form-section-card">
                    <h2 className="form-section-title">Driver &amp; Category</h2>
                    <p className="form-section-hint">
                     If this item is tied to a driver or category,   <strong>fill this section out for better search visibility.</strong>
                     
                    </p>
                    <div className="sign-up-form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={categoryNotRelevant}
                                onChange={(e) => handleCategoryNotRelevantChange(e.target.checked)}
                            />
                            Not relevant - this is a generic item
                        </label>
                    </div>

                    {categoryNotRelevant ? (
                        <p className="form-section-skipped" role="status">
                            Driver and category skipped. Buyers won’t be able to find this listing by driver or category filters.
                        </p>
                    ) : (
                    <>
                        <div className="sign-up-form-group">
                            <label htmlFor="driverDropdown">Select Driver<span className="asterisk">*</span></label>
                        </div>
                        {errors.driverName && <p className="sign-up-error">{errors.driverName}</p>}
                        <Autocomplete
                            disablePortal
                            options={drivers}
                            getOptionLabel={(option) =>
                                `${option.fullName}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedDriver || null}
                            onChange={(event, newValue) => {
                                setSelectedDriver(newValue);
                                setFormData(prev => ({ ...prev, driverName: newValue?.fullName || '' }));
                                if (newValue?.fullName) clearError('driverName');
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ marginBottom: 2 }}
                            className={errors.driverName ? "error" : ""}
                        />
                        <div className="sign-up-form-group">
                            <label htmlFor="seriesDropdown">Select Series<span className="asterisk">*</span></label>
                        </div>
                        {errors.series && <p className="sign-up-error">{errors.series}</p>}
                        <Autocomplete
                            disablePortal
                            options={series}
                            getOptionLabel={(option) =>
                                `${option.seriesName}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedSeries || null}
                            onChange={(event, newValue) => {
                                setSelectedSeries(newValue);
                                setFormData(prev => ({ ...prev, series: newValue?.seriesName || '' }));
                                if (newValue?.seriesName) clearError('series');
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ marginBottom: 2 }}
                            className={errors.series ? "error" : ""}
                        />

                    {/* BREADCRUMB SELECTION DISPLAY */}
                    <div className="breadcrumb-selections">
                        Category:
                        {selectedMainCategory && <span> {selectedMainCategory.name}</span>}

                        {selectedMainCategory?.name === "Diecasts" && selectedDiecastSubCategory && (
                            <>
                                <span> &gt; {selectedDiecastSubCategory.name}</span>
                                {selectedDiecastYearCategory && <span> &gt; {selectedDiecastYearCategory.name}</span>}
                                {selectedDiecastVersionCategory && <span> &gt; {selectedDiecastVersionCategory.name}</span>}
                            </>
                        )}

                        {selectedMainCategory?.name === "Trading Cards" && selectedTradingCardYearCategory && (
                            <>
                                <span> &gt; {selectedTradingCardYearCategory.name}</span>
                                {selectedTradingCardBrandCategory && <span> &gt; {selectedTradingCardBrandCategory.name}</span>}
                                {selectedTradingCardTypeCategory && <span> &gt; {selectedTradingCardTypeCategory.name}</span>}
                            </>
                        )}

                        {selectedMainCategory?.name === "Race Used / Team Issued" && selectedraceUsedSubCategory && (
                            <>
                                <span> &gt; {selectedraceUsedSubCategory.name}</span>
                                {selectedraceUsedSubCategory.name === "Sheet Metal / Body Panel" && selectedsheetMetalSubCategory && (
                                    <span> &gt; {selectedsheetMetalSubCategory.name}</span>
                                )}
                                {selectedraceUsedSubCategory.name === "Driver Worn" && selecteddriverWornSubCategory && (
                                    <span> &gt; {selecteddriverWornSubCategory.name}</span>
                                )}
                                {selectedraceUsedSubCategory.name === "Team Issued" && selectedTeamIssuedSubCategory && (
                                    <span> &gt; {selectedTeamIssuedSubCategory.name}</span>
                                )}
                            </>
                        )}
                    </div>
                    <br />
                
                    {/* CONDITIONAL DROPDOWN LOGIC */}
                    <>
                        <div className="sign-up-form-group">
                            <label htmlFor="mainCategoryDropdown">Select Main Category<span className="asterisk">*</span></label>
                        </div>
                        {errors.mainCategory && <p className="sign-up-error">{errors.mainCategory}</p>}
                        <Autocomplete
                            disablePortal
                            options={mainCategories}
                            getOptionLabel={(option) => `${option.name}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedMainCategory || null}
                            onChange={(event, newValue) => {
                                setSelectedMainCategory(newValue);
                                // Reset all dependent subcategories when main category changes
                                setSelectedDiecastSubCategory(null);
                                setSelectedraceUsedSubCategory(null);
                                setSelectedSheetMetalSubCategory(null);
                                setSelectedDriverWornSubCategory(null);
                                setSelectedTeamIssueSubCategory(null);
                                setSelectedDiecastYearCategory(null);
                                setSelectedDiecastVersionCategory(null);
                                setSelectedTradingCardYearCategory(null);
                                setSelectedTradingCardBrandCategory(null);
                                setSelectedTradingCardTypeCategory(null);
                                setSelectedHeroCardYearCategory(null);
                                setSelectedProgramTicketYearCategory(null);
                                setFormData(prev => ({ ...prev, mainCategory: newValue?.name || '' }));
                                if (newValue?.name) clearError('mainCategory');
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ marginBottom: 2 }}
                            className={errors.mainCategory ? "error" : ""}
                        />
                    </>

                    {selectedMainCategory?.name === "Diecasts" && (
                        <>
                            <div className="sign-up-form-group">
                                <label htmlFor="diecastSubcategoryDropdown">Select Diecast Subcategory</label>
                            </div>
                            <Autocomplete
                                disablePortal
                                options={diecastSubCategories}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedDiecastSubCategory || null}
                                onChange={(event, newValue) => {
                                    setSelectedDiecastSubCategory(newValue);
                                    setFormData(prev => ({ ...prev, subCategory: newValue?.name || '', subSubCategory: '', subSubSubCategory: '' }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                                sx={{ marginBottom: 2 }}
                            />

                            {selectedMainCategory?.name === "Diecasts" && selectedDiecastSubCategory && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="diecastYearCategoryDropdown">Select Diecast Year</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={diecastYearCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedDiecastYearCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedDiecastYearCategory(newValue);
                                            setSelectedDiecastVersionCategory(null);
                                            setFormData(prev => ({ ...prev, subSubCategory: newValue?.name || '', subSubSubCategory: '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}

                            {selectedMainCategory?.name === "Diecasts" && selectedDiecastSubCategory && selectedDiecastYearCategory && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="diecastVersionCategoryDropdown">Select Diecast Version</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={diecastVersionCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedDiecastVersionCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedDiecastVersionCategory(newValue);
                                            setFormData(prev => ({ ...prev, subSubSubCategory: newValue?.name || '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {selectedMainCategory?.name === "Trading Cards" && (
                        <>
                            <div className="sign-up-form-group">
                                <label htmlFor="tradingCardYearDropdown">Select Trading Card Year</label>
                            </div>
                            <Autocomplete
                                disablePortal
                                options={tradingCardYearCategories}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedTradingCardYearCategory || null}
                                onChange={(event, newValue) => {
                                    setSelectedTradingCardYearCategory(newValue);
                                    setSelectedTradingCardBrandCategory(null);
                                    setSelectedTradingCardTypeCategory(null);
                                    setFormData(prev => ({ ...prev, subCategory: newValue?.name || '', subSubCategory: '', subSubSubCategory: '' }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                                sx={{ marginBottom: 2 }}
                            />

                            {selectedTradingCardYearCategory && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="tradingCardBrandCategoryDropdown">Select Trading Card Brand</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={tradingCardBrandCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedTradingCardBrandCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedTradingCardBrandCategory(newValue);
                                            setSelectedTradingCardTypeCategory(null);
                                            setFormData(prev => ({ ...prev, subSubCategory: newValue?.name || '', subSubSubCategory: '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}

                            {selectedTradingCardYearCategory && selectedTradingCardBrandCategory && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="tradingCardTypeCategoryDropdown">Select Trading Card Type</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={tradingCardTypeCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedTradingCardTypeCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedTradingCardTypeCategory(newValue);
                                            setFormData(prev => ({ ...prev, subSubSubCategory: newValue?.name || '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {selectedMainCategory?.name === "Race Used / Team Issued" && (
                        <>
                            <div className="sign-up-form-group">
                                <label htmlFor="raceUsedSubCategoryDropdown">Select Race Used Subcategory</label>
                            </div>
                            <Autocomplete
                                disablePortal
                                options={raceUsedSubCategories}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedraceUsedSubCategory || null}
                                onChange={(event, newValue) => {
                                    setSelectedraceUsedSubCategory(newValue);
                                    setSelectedSheetMetalSubCategory(null);
                                    setSelectedDriverWornSubCategory(null);
                                    setSelectedTeamIssueSubCategory(null);
                                    setSelectedDiecastYearCategory(null);
                                    setSelectedDiecastVersionCategory(null);
                                    setSelectedTradingCardYearCategory(null);
                                    setSelectedTradingCardBrandCategory(null);
                                    setSelectedTradingCardTypeCategory(null);
                                    setFormData(prev => ({ ...prev, subCategory: newValue?.name || '', subSubCategory: '', subSubSubCategory: '' }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                                sx={{ marginBottom: 2 }}
                            />

                            {selectedraceUsedSubCategory?.name === "Sheet Metal / Body Panel" && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="sheetMetalSubCategoryDropdown">Select Sheet Metal Subcategory</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={sheetMetalSubCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedsheetMetalSubCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedSheetMetalSubCategory(newValue);
                                            setFormData(prev => ({ ...prev, subSubCategory: newValue?.name || '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}

                            {selectedraceUsedSubCategory?.name === "Team Issued" && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="teamIssuedSubCategoryDropdown">Select Team Issued Subcategory</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={teamIssuedSubCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selectedTeamIssuedSubCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedTeamIssueSubCategory(newValue);
                                            setFormData(prev => ({ ...prev, subSubCategory: newValue?.name || '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}

                            {selectedraceUsedSubCategory?.name === "Driver Worn" && (
                                <>
                                    <div className="sign-up-form-group">
                                        <label htmlFor="driverWornSubCategoryDropdown">Select Driver Worn Subcategory</label>
                                    </div>
                                    <Autocomplete
                                        disablePortal
                                        options={driverWornSubCategories}
                                        getOptionLabel={(option) => `${option.name}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={selecteddriverWornSubCategory || null}
                                        onChange={(event, newValue) => {
                                            setSelectedDriverWornSubCategory(newValue);
                                            setFormData(prev => ({ ...prev, subSubCategory: newValue?.name || '' }));
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        sx={{ marginBottom: 2 }}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {selectedMainCategory?.name === "Hero Cards / Post Cards" && (
                        <>
                            <div className="sign-up-form-group">
                                <label htmlFor="herocardYearCategoryDropdown">Select Year</label>
                            </div>
                            <Autocomplete
                                disablePortal
                                options={heroCardYearCategories}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedHeroCardYearCategory || null}
                                onChange={(event, newValue) => {
                                    setSelectedHeroCardYearCategory(newValue);
                                    setFormData(prev => ({ ...prev, subCategory: newValue?.name || '' }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                                sx={{ marginBottom: 2 }}
                            />
                        </>
                    )}

                    {selectedMainCategory?.name === "Programs / Tickets" && (
                        <>
                            <div className="sign-up-form-group">
                                <label htmlFor="programTicketYearCategoryDropdown">Select Year</label>
                            </div>
                            <Autocomplete
                                disablePortal
                                options={progamTicketYearCategories}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedProgramTicketYearCategory || null}
                                onChange={(event, newValue) => {
                                    setSelectedProgramTicketYearCategory(newValue);
                                    setFormData(prev => ({ ...prev, subCategory: newValue?.name || '' }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                                sx={{ marginBottom: 2 }}
                            />
                        </>
                    )}
                    </>
                    )}

                    </section>

                    <div className="cmi-form-actions">
                        {sourceCollectionItemId ? (
                            <button
                                type="button"
                                className="login-submit-btn msv-shine"
                                disabled={loadingSourceCollectible}
                                onClick={continueToListingSummary}
                            >
                                Continue to price &amp; shipping
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={`login-submit-btn msv-shine${submitting ? ' disabled' : ''}`}
                                disabled={submitting || loadingSourceCollectible}
                                onClick={requestSubmitConfirm}
                            >
                                {submitting
                                    ? (sourceCollectionItemId ? 'Listing for sale…' : 'Adding to Marketplace…')
                                    : (sourceCollectionItemId ? 'List for Sale' : 'Add To Marketplace')}
                            </button>
                        )}
                    </div>
                </form>
                {successMessage && <p className="sign-up-success">{successMessage}</p>}
            </div>
    );
};

export default CreateMarketplaceItem;